import { useTranslation } from 'react-i18next';
import { Box, useTheme } from '@mui/material';

import { Typography } from '@zendesk/forethought-elements';
import { AuditPayload, ToolCallStatus } from './auditData';
import { getPayloadAbsenceLabel } from './auditLabels';
import { CodeBlock } from 'src/pages/events/error/CodeBlock';

const stringify = (value: unknown) =>
  typeof value === 'string' ? value : JSON.stringify(value, null, 2);

const isEmptyPayload = (value: unknown) =>
  (typeof value === 'string' && value.trim() === '') ||
  (Array.isArray(value) && value.length === 0) ||
  (typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    Object.keys(value).length === 0);

const ToolCallHeader = ({ status }: { status?: ToolCallStatus }) => {
  const { t } = useTranslation('intent-conversation-analytics');
  const { palette } = useTheme();
  const isError = status === 'error';

  return (
    <Box sx={{ alignItems: 'center', display: 'flex', gap: '6px' }}>
      <Typography variant='font12Medium'>
        {t('conversationAudit.toolCall')}
      </Typography>
      {status && (
        <>
          <Typography color={palette.colors.grey[500]} variant='font12'>
            ·
          </Typography>
          <Box
            sx={{
              bgcolor: isError
                ? palette.colors.red[100]
                : palette.colors.green[100],
              borderRadius: '4px',
              px: '6px',
              py: '1px',
            }}
          >
            <Typography
              color={
                isError ? palette.colors.red[700] : palette.colors.green[700]
              }
              variant='font11'
            >
              {isError
                ? t('conversationAudit.toolCallError')
                : t('conversationAudit.toolCallCompleted')}
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );
};

// One half of the payload. Missing renders the reason it is missing (an absence
// state); present-but-empty says so in a line instead of an empty code block.
const PayloadSection = ({
  absence,
  emptyLabel,
  title,
  value,
}: {
  absence: string;
  emptyLabel: string;
  title: string;
  value: unknown;
}) => {
  const { t } = useTranslation('intent-conversation-analytics');
  const { palette } = useTheme();

  if (value === undefined || value === null) {
    return <CodeBlock content={absence} title={title} />;
  }

  if (isEmptyPayload(value)) {
    return (
      <Box sx={{ columnGap: '4px', display: 'flex', flexWrap: 'wrap' }}>
        <Typography variant='font11'>
          {t('common.labelWithColon', { label: title })}
        </Typography>
        <Typography color={palette.colors.grey[600]} variant='font11'>
          {emptyLabel}
        </Typography>
      </Box>
    );
  }

  return <CodeBlock content={stringify(value)} title={title} />;
};

// Request and response as separate labelled code blocks, the same treatment the
// Error Log page gives a payload.
export const PayloadDetails = ({ payload }: { payload: AuditPayload }) => {
  const { t } = useTranslation('intent-conversation-analytics');

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', gap: '12px', pt: '4px' }}
    >
      {payload.toolCall && <ToolCallHeader status={payload.toolCall.status} />}
      <PayloadSection
        absence={getPayloadAbsenceLabel(
          t,
          payload.requestAbsence ?? 'unavailable',
        )}
        emptyLabel={t('conversationAudit.payloadRequestEmpty')}
        title={t('conversationAudit.payloadRequest')}
        value={payload.request}
      />
      <PayloadSection
        absence={getPayloadAbsenceLabel(
          t,
          payload.responseAbsence ?? 'unavailable',
        )}
        emptyLabel={t('conversationAudit.payloadResponseEmpty')}
        title={t('conversationAudit.payloadResponse')}
        value={payload.response}
      />
    </Box>
  );
};
